import { FC } from 'react';
import {
  Box,
  BoxProps,
  styled,
  Tab as MuiTab,
  tabClasses,
  TabProps as MuiTabProps,
  Tabs as MuiTabs,
  tabsClasses,
  TabsProps as MuiTabsProps,
} from '@mui/material';

import { TabContextProviderProps } from '@components/base';

import useTabContext from './useTabContext';

export type TabButtonsProps = {
  tabRowWrapperProps?: BoxProps;
};

type StyledTabButtonRowProps = BoxProps & Pick<TabContextProviderProps, 'buttonVariant'>;

const StyledTabButtonRow = styled(({ buttonVariant, ...props }: StyledTabButtonRowProps) => (
  <Box {...props} />
))(({ theme, buttonVariant }) =>
  buttonVariant === 'standard' ? { borderBottom: `thin solid ${theme.palette.divider}` } : undefined
);

type StyledTabsProps = MuiTabsProps & Pick<TabContextProviderProps, 'buttonVariant'>;

const StyledTabs = styled(({ buttonVariant, ...props }: StyledTabsProps) => <MuiTabs {...props} />)(
  ({ theme, buttonVariant }) =>
    buttonVariant === 'standard'
      ? undefined
      : {
          padding: theme.spacing(2),
          [`.${tabsClasses.indicator}`]: {
            display: 'none',
          },
        }
);

type StyledTabProps = MuiTabProps & Pick<TabContextProviderProps, 'buttonVariant'>;

const StyledTab = styled(({ buttonVariant, ...props }: StyledTabProps) => <MuiTab {...props} />)(
  ({ theme, buttonVariant }) =>
    buttonVariant === 'standard'
      ? undefined
      : {
          background: theme.palette.background.paper,
          minWidth: 0,
          minHeight: 0,
          padding: theme.spacing(),
          borderRadius: theme.shape.borderRadius,
          '&:not(:first-of-type)': {
            marginLeft: theme.spacing(0.75),
          },
          [`&.${tabClasses.selected}`]: {
            background: theme.palette.text.primary,
            color: theme.palette.getContrastText(theme.palette.text.primary),
          },
          [`&:not(.${tabClasses.selected})`]: {
            opacity: 1,
          },
        }
);

export const TabButtons: FC<TabButtonsProps> = ({ tabRowWrapperProps }) => {
  const { getContentId, getLabelId, onTabChange, selectedTab, tabs, buttonVariant } =
    useTabContext();

  return (
    <StyledTabButtonRow buttonVariant={buttonVariant} {...tabRowWrapperProps}>
      <StyledTabs
        buttonVariant={buttonVariant}
        onChange={(_, val) => onTabChange(val)}
        scrollButtons='auto'
        value={selectedTab}
        variant='scrollable'
      >
        {tabs.map(({ title, value, content, BoxProps, wrapperProps, ...rest }) => (
          <StyledTab
            {...rest}
            aria-controls={getContentId(value)}
            buttonVariant={buttonVariant}
            id={getLabelId(value)}
            key={value}
            label={title}
            value={value}
          />
        ))}
      </StyledTabs>
    </StyledTabButtonRow>
  );
};
